export type MinimalRegistrationForPricing = {
  id: string;
  order_group_id?: string | null;
  registration_amount?: number | null; // dollars
  pricing_breakdown?: {
    base_price?: number; // cents
    discount?: number; // cents
  } | null;
};

type GroupPricing = {
  shouldNormalize: boolean;
  perTeamBaseCents: number;
  perTeamDiscountCents: number;
};

const asCents = (dollars: number | null | undefined) => Math.round((dollars || 0) * 100);

export const computeGroupPricing = (
  registrations: MinimalRegistrationForPricing[]
): GroupPricing => {
  if (registrations.length < 2) {
    return { shouldNormalize: false, perTeamBaseCents: 0, perTeamDiscountCents: 0 };
  }

  const bases = registrations
    .map((r) => r.pricing_breakdown?.base_price)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
  const discounts = registrations
    .map((r) => r.pricing_breakdown?.discount)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0);

  const regAmounts = registrations
    .map((r) => asCents(r.registration_amount))
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);

  // CRITICAL: Only normalize if all teams have the SAME base_price (same division)
  // Different divisions have legitimately different prices - don't normalize those!
  if (bases.length > 1) {
    const firstBase = bases[0];
    const allSameBasePrice = bases.every((bp) => bp === firstBase);
    if (!allSameBasePrice) {
      // Different base prices = different divisions, no normalization needed
      return { shouldNormalize: false, perTeamBaseCents: 0, perTeamDiscountCents: 0 };
    }
  }

  const minDiscount = discounts.length ? Math.min(...discounts) : 0;

  // Normalize by base_price when a row stores order-level base/discount instead of per-team
  let shouldNormalizeBase = false;
  let minBase = 0;
  if (bases.length) {
    minBase = Math.min(...bases);
    const maxBase = Math.max(...bases);
    shouldNormalizeBase = maxBase >= minBase * registrations.length;
  }

  // Normalize by registration_amount when a row stores the combined net amount (after discount)
  let shouldNormalizeAmount = false;
  let minRegAmount = 0;
  if (regAmounts.length) {
    minRegAmount = Math.min(...regAmounts);
    const maxRegAmount = Math.max(...regAmounts);
    shouldNormalizeAmount = maxRegAmount >= minRegAmount * registrations.length;
  }

  const shouldNormalize = shouldNormalizeBase || shouldNormalizeAmount;

  // Prefer a true per-team base_price when available; otherwise derive base from (min net + min discount)
  const perTeamBaseCents = minBase > 0 ? minBase : Math.max(0, minRegAmount + minDiscount);

  return {
    shouldNormalize,
    perTeamBaseCents,
    perTeamDiscountCents: minDiscount,
  };
};

const sum = (nums: number[]) => nums.reduce((a, b) => a + b, 0);

const isNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const trySplitCombinedRow = (
  registration: MinimalRegistrationForPricing,
  group: MinimalRegistrationForPricing[]
): {
  regAmountCents: number;
  discountCents: number;
  basePriceCents: number;
} | null => {
  if (group.length < 2) return null;

  const others = group.filter((g) => g.id !== registration.id);
  if (others.length === 0) return null;

  const thisRegAmount = asCents(registration.registration_amount);
  const otherRegAmounts = others.map((r) => asCents(r.registration_amount)).filter((v) => v > 0);

  const thisBase = registration.pricing_breakdown?.base_price;
  const thisDiscount = registration.pricing_breakdown?.discount;
  const otherBases = others
    .map((r) => r.pricing_breakdown?.base_price)
    .filter((v): v is number => isNumber(v) && v > 0);
  const otherDiscounts = others
    .map((r) => r.pricing_breakdown?.discount)
    .filter((v): v is number => isNumber(v) && v >= 0);

  // Heuristic: when one row stores the combined order totals, we can derive its per-team
  // amount by subtracting the sum of the other rows.
  const tolerance = 1.05; // 5% wiggle room for rounding/fees (this function is for registration/base/discount only)

  // 1) Prefer splitting by registration_amount (net)
  if (thisRegAmount > 0 && otherRegAmounts.length === others.length) {
    const sumOthers = sum(otherRegAmounts);
    if (sumOthers > 0 && thisRegAmount >= sumOthers * tolerance) {
      const regAmountCents = Math.max(0, thisRegAmount - sumOthers);

      // Try to split discount/base similarly; fall back to deriving base from (net + discount)
      const sumOtherDiscounts = sum(otherDiscounts);
      const discountCents =
        isNumber(thisDiscount) && otherDiscounts.length === others.length && thisDiscount >= sumOtherDiscounts * tolerance
          ? Math.max(0, thisDiscount - sumOtherDiscounts)
          : isNumber(thisDiscount)
            ? Math.max(0, thisDiscount)
            : 0;

      const sumOtherBases = sum(otherBases);
      const basePriceCents =
        isNumber(thisBase) && otherBases.length === others.length && thisBase >= sumOtherBases * tolerance
          ? Math.max(0, thisBase - sumOtherBases)
          : Math.max(0, regAmountCents + discountCents);

      return { regAmountCents, discountCents, basePriceCents };
    }
  }

  // 2) Otherwise, split by base/discount if they look combined
  if (isNumber(thisBase) && otherBases.length === others.length) {
    const sumOtherBases = sum(otherBases);
    if (sumOtherBases > 0 && thisBase >= sumOtherBases * tolerance) {
      const basePriceCents = Math.max(0, thisBase - sumOtherBases);

      const sumOtherDiscounts = sum(otherDiscounts);
      const discountCents =
        isNumber(thisDiscount) && otherDiscounts.length === others.length && thisDiscount >= sumOtherDiscounts * tolerance
          ? Math.max(0, thisDiscount - sumOtherDiscounts)
          : isNumber(thisDiscount)
            ? Math.max(0, thisDiscount)
            : 0;

      const regAmountCents = Math.max(0, basePriceCents - discountCents);
      return { regAmountCents, discountCents, basePriceCents };
    }
  }

  return null;
};

/**
 * Returns the registration's pricing directly from the database.
 * 
 * NOTE: The source fix in Step5Review.tsx now ensures each team's registration_amount
 * is stored correctly per-team. We no longer need display-time normalization.
 * 
 * The `group` parameter is kept for API compatibility but is no longer used.
 */
export const getNormalizedRegistrationPricing = (
  registration: MinimalRegistrationForPricing,
  _group: MinimalRegistrationForPricing[]
) => {
  const pricing = registration.pricing_breakdown || {};
  const regAmountCents = asCents(registration.registration_amount);
  const discountCents = pricing.discount || 0;
  const basePriceCents = pricing.base_price || (regAmountCents + discountCents);

  return {
    isNormalized: false,
    regAmountCents,
    discountCents,
    basePriceCents,
  };
};

